import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import React, { useRef } from 'react';

import { useCadastroDecomposicao } from '../CadastroDeDecomposicaoContext';
import { formatters } from '../utils/formatters';
import { ColumnSeparator } from './styles';
import { formatCurrencyAsText, transformAsCurrency } from '~/utils/functions';

export interface CustomGridColumns {
  field: string;
  headerName: string;
  width: number;
  minWidth?: number;
  align?: 'center' | 'right' | 'inherit' | 'justify' | 'left';
  headerAlign?: 'center' | 'right' | 'left';
  format?: string;
  fixed?: boolean;
  sortable?: boolean;
  disableColumnMenu?: boolean;
  renderCell?: (row: any) => void;
  fixedDirection?: string;
}

interface FormProps {
  rows: any[];
  columns: CustomGridColumns[];
}

/**
 * @param rows Array de objetos contendo as linhas da tabela (as keys dos objetos devem ser iguais as ids das colunas)
 * @param columns Array de objetos contendo as colunas
 * @param columns Para fixar uma coluna basta passar uma chave chamada fixed com o valor true e Left com 0 (também é necessário passar um width e um minWidth)
 * @param columns Caso mais de uma coluna seja fixa, a coluna seguinte deverá receber o valor de Left igual ao minWidth da coluna anterior
 * @returns Componente DataGrid personalizado
 */

const CustomDataGrid: React.FC<FormProps> = (props) => {
  const { rows, columns } = props;

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const tableRef = useRef<any>();
  const { isUnitaryType } = useCadastroDecomposicao();

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  function formatType(item: any, type: string | undefined, isUnitary: boolean) {
    switch (type) {
      case 'dta':
        return formatters.formatDate(item);
      case 'dtatime':
        return formatters.formatDateTime(item);
      case 'number':
        return formatters.formatNumber(item);
      case 'percent':
        return formatters.formatPercent(item, isUnitary);
      case 'qtd':
        return formatCurrencyAsText(transformAsCurrency(item), 3);
      default:
        return item;
    }
  }

  return (
    <>
      <Paper
        style={{
          width: '100%',
          height: '100%',
          flex: 1,
          zIndex: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <TableContainer ref={tableRef}>
          <Table stickyHeader>
            <TableHead style={{ position: 'sticky', top: '0' }}>
              <TableRow>
                {columns.map((column) => {
                  return (
                    <TableCell
                      align={column.headerAlign}
                      key={column.field}
                      className="text-truncate"
                      style={{
                        minWidth: column.minWidth
                          ? column.minWidth
                          : column.width,
                        position: 'relative',
                      }}
                    >
                      {column.headerName}
                      <ColumnSeparator>
                        <svg
                          className="MuiSvgIcon-root MuiDataGrid-iconSeparator"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path d="M11 19V5h2v14z" />
                        </svg>
                      </ColumnSeparator>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows

                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, rowIndex) => {
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      // eslint-disable-next-line react/no-array-index-key
                      key={rowIndex}
                    >
                      {columns.map((column) => {
                        if (column.fixed)
                          return (
                            <TableCell
                              key={column.field}
                              style={{
                                position: 'sticky',
                                right: 0,
                                width: column.width,
                                minWidth: column.width,
                                background: '#ffffff',
                                zIndex: 0,
                              }}
                            >
                              {column.renderCell
                                ? column.renderCell(row)
                                : formatType(
                                    row[column.field],
                                    column?.format,
                                    isUnitaryType,
                                  )}
                            </TableCell>
                          );
                        return (
                          <TableCell align={column.align} key={column.field}>
                            {column.renderCell
                              ? column.renderCell(row)
                              : formatType(
                                  row[column.field],
                                  column?.format,
                                  isUnitaryType,
                                )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[-1]}
          onPageChange={handleChangePage}
          count={rows.length}
          page={page}
          rowsPerPage={rowsPerPage}
        />
      </Paper>
    </>
  );
};

export default CustomDataGrid;
